import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '~/client/src/shared/components/shadcn-ui/avatar'
import User from '~/client/src/shared/models/User'

import { ResourceCardFieldIconKind } from '../definitions/types'

/**
 * Icons a card field derives from its own value rather than a static glyph.
 *
 * Both read their store from context rather than `inject`, so a tree without a Provider
 * degrades to the fallback instead of throwing, and both observe it so the image appears
 * once the store loads.
 *
 * The fallback is rendered *inside* the component, not chosen by the caller: whether an
 * image exists is only known after the store lookup, and by then the mapper has already
 * committed an element. Returning null here instead would leave the meta row's 20px icon
 * box empty — which is exactly what it did before.
 */

// Fills whatever icon box ObjectReference gives it, so the avatar and the glyph it falls
// back to always occupy the same footprint. No `size` prop: that adds a data-variant rule
// which would outrank this class.
const AVATAR_CLASS = 'tw:size-full'

interface IValueIconProps {
  id: string
  /** Shown when the store has no image for this id — the field's static glyph. */
  fallback?: React.ReactNode
}

const MemberAvatarIcon: React.FC<IValueIconProps> = observer(
  ({ id, fallback }) => {
    const { projectMembersStore } = React.useContext(MobXProviderContext)
    const member = projectMembersStore?.getById(id)
    if (!member) return <>{fallback}</>

    return (
      <Avatar className={AVATAR_CLASS}>
        {!!member.avatarUrl && (
          <AvatarImage src={member.avatarUrl} alt={User.getFullName(member)} />
        )}
        <AvatarFallback className="tw:text-[9px] tw:font-medium">
          {User.getInitialsToDisplay(member)}
        </AvatarFallback>
      </Avatar>
    )
  },
)

const CompanyLogoIcon: React.FC<IValueIconProps> = observer(
  ({ id, fallback }) => {
    const { companiesStore } = React.useContext(MobXProviderContext)
    const company = companiesStore?.getCompanyById(id)
    // avatarUrl before logoUrl, matching how the user directory picks a company image.
    const logo = company?.avatarUrl || company?.logoUrl
    if (!logo) return <>{fallback}</>

    return (
      <Avatar className={AVATAR_CLASS}>
        <AvatarImage src={logo} alt={company.name} />
      </Avatar>
    )
  },
)

// A location's own type glyph, tinted with its attribute colour — the same pair the
// location resource-link registry builds for group headers. LocationIcon is what
// LocationReference wraps, so a location meta row and a LocationReference draw the same
// artwork from the same ICON_DATA table. Read from the deleted-aware map so a card naming
// a since-deleted location still shows its type rather than falling back to a generic pin.
const LocationTypeIcon: React.FC<IValueIconProps> = observer(
  ({ id, fallback }) => {
    const { locationAttributesStore } = React.useContext(MobXProviderContext)
    const location = locationAttributesStore?.allItemsMapWithDeleted?.[id]
    if (!location?.iconName) return <>{fallback}</>

    return (
      <LocationIcon
        iconName={location.iconName}
        color={location.color || undefined}
        className="tw:size-full"
      />
    )
  },
)

/** Element factory so the (pure, non-JSX) card mapper can emit these. */
export function renderFieldValueIcon(
  iconKind: ResourceCardFieldIconKind,
  value: unknown,
  fallback?: React.ReactNode,
): React.ReactNode {
  if (value === null || value === undefined || value === '') return undefined

  const id = String(value)

  switch (iconKind) {
    case 'avatar':
      return <MemberAvatarIcon id={id} fallback={fallback} />
    case 'companyLogo':
      return <CompanyLogoIcon id={id} fallback={fallback} />
    case 'location':
      return <LocationTypeIcon id={id} fallback={fallback} />
  }
}
